import { Box, TextField } from "@mui/material";
import React, { useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setState, setTotalValues } from "./defineSlice";
import "./Define.css";
import {
	formatNumber,
	removeComma,
	isWeekColumnEditable,
	rowDepletionModelKey,
	shouldShowBlankWeekPlaceholder,
	findGridRowByCustomerSku,
	commitGridDataUpdate,
} from "./defineUtils";
import debounce from 'lodash/debounce';
import { QUOTA_CELL_DEBOUNCE_MS, useQuotaCellInputSync } from "./useQuotaCellInput";

export default function TableCellWeek2(params) {
   const dispatch = useDispatch();
   const define = useSelector((state) => state.define);
   const { gridData, gridDataFiltered, selectedDepletionPeriodType } = define;
   const gridDataRef = useRef(gridData);
   const gridDataFilteredRef = useRef(gridDataFiltered);
   gridDataRef.current = gridData;
   gridDataFilteredRef.current = gridDataFiltered;
   const rowDm = rowDepletionModelKey(params.row);
   const cellInput = useQuotaCellInputSync(params.row.weekly2);
   const { setDraft, displayValue, onFocus, onBlur } = cellInput;
   const customerID = params.row.customerID;
   const customerGroupID = params.row.customerGroupID;
   const productGroupID = params.row.productGroupID;
   const skuid = params.row.skuid;

   function updateGrid(hasError, w) {
      let updatedItem = null;
      const filtered = gridDataFilteredRef.current;
      const fullGrid = gridDataRef.current;
      const updated = filtered.map((item, index) => {
         const flag =
            item.customerID === customerID &&
            item.customerGroupID === customerGroupID &&
            item.productGroupID === productGroupID &&
            item.skuid === skuid;
         if (flag === true) {
            let quota = Number(item.quota);
            const dm = rowDepletionModelKey(item);
            if (dm === "biweekly") {
               if (w !== quota) {
                  quota = w;
               }
               dispatch(setState("isFailed", true));
               dispatch(
                  setState(
                     "isFailed_Info",
                     "Quota value has been updated as second half value was edited."
                  )
               );
            }
            updatedItem = {
               ...item,
               hasError,
               quota,
               weekly2: !w ? 0 : w,
            };
            return updatedItem
         }
         return item;
      });

      const updatedGrid = [];
      for (let i = 0; i < fullGrid.length; ++i) {
         const item = fullGrid[i];
         if(item.id === updatedItem.id) {
            updatedGrid.push(updatedItem);
         }
         else {
            updatedGrid.push(item);
         }
      }
      commitGridDataUpdate(dispatch, setState, {
         gridData: updatedGrid,
         gridDataFiltered: updated,
         updatedRow: updatedItem,
         backupFields:
            rowDepletionModelKey(updatedItem) === "biweekly"
               ? ["weekly2", "quota"]
               : ["weekly2"],
      });
   }

   // function updateWeek2(event) {
   //    let w2 = removeComma(event.target.value);
   //    w2 = Number(w2);
   //    let w1 = Number(params.row.weekly1);
   //    let w3 = Number(params.row.weekly3);
   //    let quota = Number(params.row.quota);
   //    let hasError = false;
   //    setWeek2(w2);

   //    if (!w2 && w2 !== 0) {
   //       dispatch(setState("isError", true));
   //       dispatch(setState("isError_Info", "Only numbers are allowed."));
   //       return;
   //    }

   //    if (selectedDepletion.key === "biweekly") {
   //       if (w1 > w2) {
   //          hasError = true;
   //       }
   //    } else {
   //       if (w1 > w2) {
   //          hasError = true;
   //       } else if (w2 > w3) {
   //          hasError = true;
   //       }
   //    }
   //    dispatch(setState("gridDataChanged", true));
   //    updateGrid(hasError, w2, quota);
   //    dispatch(setTotalValues());
   // }

   const debouncedUpdate = useMemo(
      () =>
         debounce((hasError, w2) => {
            dispatch(setState("gridDataChanged", true));
            updateGrid(hasError, w2);
            dispatch(setTotalValues());
         }, QUOTA_CELL_DEBOUNCE_MS),
      [
         params.row?.customerID,
         params.row?.customerGroupID,
         params.row?.productGroupID,
         params.row?.skuid,
         params.row?.depletionModel,
         dispatch,
      ]
   );

   useEffect(() => {
      return () => debouncedUpdate.cancel();
   }, [debouncedUpdate]);

   function updateWeek2(event) {
      let raw = removeComma(event.target.value);
      setDraft(raw);
      let w2 = Number(raw);
      const live =
         findGridRowByCustomerSku(gridDataFilteredRef.current, params.row) ??
         findGridRowByCustomerSku(gridDataRef.current, params.row) ??
         params.row;
      const dmLive = rowDepletionModelKey(live);
      let w3 = Number(live.weekly3);
      let quota = Number(live.quota);
      let hasError = false;

      if (!w2 && w2 !== 0) {
         dispatch(setState("isError", true));
         dispatch(setState("isError_Info", "Only numbers are allowed."));
         return;
      }

      if (dmLive === "none") {
         // no row validation for this column
      } else if (dmLive === "biweekly") {
         if (w2 !== quota) {
            hasError = true;
         }
      } else if (w2 > w3) {
         hasError = true;
      }
      debouncedUpdate(hasError,w2);
   }   

   function handleValidity(w1, w2, w3){
      const quota = Number(params.row.quota);
      if (rowDm === "none") {
         return;
      }
      if (rowDm === "biweekly") {
         if (Number(w2) !== quota) {
            dispatch(setState("isError", true));
            dispatch(
               setState(
                  "isError_Info",
                  "Must equal quota"
               )
            );
         }
      } else if (Number(w2) > Number(w3)) {
         dispatch(setState("isError", true));
         dispatch(
            setState("isError_Info", "Must be less or equal to Week3")
         );
      }
   }

   if (shouldShowBlankWeekPlaceholder(2, params.row, gridDataFiltered)) {
      return <Box sx={{ width: 100 }} />;
   }

   return (
      <TextField
         disabled={
            !isWeekColumnEditable(
               2,
               params.row,
               selectedDepletionPeriodType
            )
         }
         variant="standard"
         value={displayValue}
         className={
            rowDm === "none"
               ? ""
               : rowDm === "biweekly"
                  ? Number(params.row.weekly2) !== Number(params.row.quota)
                     ? "red-row"
                     : ""
                  : Number(params.row.weekly2) > Number(params.row.weekly3)
                     ? "red-row"
                     : ""
         }
         onChange={updateWeek2}
         onFocus={onFocus}
         onBlur={() => {
            debouncedUpdate.flush();
            onBlur();
            handleValidity(params.row.weekly1, params.row.weekly2, params.row.weekly3);
         }}
         sx={{ width: 100 }}
      />
   );
}
